import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ExternalLink, X } from "lucide-react";
import CustomLoader from "../../components/CustomLoader";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import discord from "../../assests/Icons/discord.svg";
import { getUsers } from "../../utils/getUser";
import { getUserByid, updateDiscordRole, updateUserProfile } from "../../reducers/authentication.slice";
import { AuthContext } from "../../context/authContext";

export default function LinkDiscordPage() {
  const [showLoader, setShowLoader] = useState(false);
  const [discordId, setDiscordId] = useState(
    localStorage?.getItem("discordId") ? localStorage.getItem("discordId") : ""
  );
  const [statusMessage, setStatusMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [statusCode, setStatusCode] = useState("");
  const [backendDiscordUsername, setBackendDiscordUsername] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useContext(AuthContext);

  const handleUpdateDiscordRole = () => {
    setShowLoader(true);
    const payload = {
      id: user?.id,
      discord_id: backendDiscordUsername,
    };
    dispatch(updateDiscordRole(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const newUserData = {
        ...getUsers(),
        discord_id: res?.payload?.discord_id,
        discord_role: res?.payload?.discord_role_names,
        is_discord_verified: res?.payload?.is_discord_verified,
      };
      localStorage.setItem("user", JSON.stringify(newUserData));
      setShowLoader(false);
      navigate("/user-dashboard");
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status_code = params.get("status_code");
    const discord_username = params.get("discord_username");
    if (discord_username) {
      setBackendDiscordUsername(discord_username);
    }
    if (!status_code) {
      return;
    }
    if (discord_username !== discordId) {
      setError(true);
      setErrorMessage(
        "The discord username you entered does not match the account you are signing in with."
      );
    }
    setStatusCode(status_code);
    if (status_code === "DIS_5") {
      setStatusMessage("Discord account not found/ Code is invalid/expired.");
      setShowMessage(true);
    } else if (status_code === "DIS_2") {
      setStatusMessage(
        <>
          Your Discord account has not joined the RSC Discord Server. Please
          join our server by clicking{" "}
          <a
            href="https://discord.gg/J3Qxq4a2nq"
            target="_blank"
            rel="noopener noreferrer"
          >
            HERE
          </a>
          . Once that’s done, you can complete the linking process.
        </>
      );
      setShowMessage(true);
    } else if (status_code === "DIS_3") {
      setStatusMessage(
        `The discord account "${discord_username}" used to authenticate is part of RSC server but we found multiple accounts with the same discord username in the database.`
      );
      setShowMessage(true);
    } else if (status_code === "DIS_4") {
      setStatusMessage(
        `The discord account "${discord_username}" used to authenticate is part of RSC server but there are no users linked with this discord username. 
        Please click on below button to link ${discord_username} with your account.`
      );
      setShowMessage(true);
    } else if (status_code === "DIS_1") {
      setShowLoader(true);
      const userId = getUsers()?.id;
      const payload = {
        userId,
      };
      dispatch(getUserByid(payload)).then((res) => {
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          setShowLoader(false);
          showToast(res?.error?.message, "error");
          return;
        }
        const newUserData = {
          ...getUsers(),
          discord_id: res?.payload?.discord_id,
          discord_role: res?.payload?.discord_role,
          permission_details: res?.payload?.permission_details,
          address_details: res?.payload?.address_details,
          payment_details: res?.payload?.payment_details,
          first_name: res?.payload?.first_name,
          last_name: res?.payload?.last_name,
          profile_picture: res?.payload?.profile_picture,
          role: res?.payload?.role,
          ip_address: res?.payload?.ip_address,
          last_login: res?.payload?.last_login,
          date_of_joining: res?.payload?.date_of_joining,
          login_type: res?.payload?.login_type,
          is_discord_verified: res?.payload?.is_discord_verified,
        };
        localStorage.setItem("user", JSON.stringify(newUserData));
        setShowLoader(false);
        navigate("/user-dashboard");
      });
    }
  }, [location]);

  const user = getUsers();

  const handleRedirectDiscord = () => {
    setShowLoader(true);
    localStorage.setItem("discordId", discordId);
    const payload = {
      id: user?.id,
      discord_id: discordId,
    };
    dispatch(updateUserProfile(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      window.location.href = process.env.REACT_APP_DISCORD_REDIRECT_URL;
    });
  };

  const validateDiscordUsername = (value) => {
    const regex = /^[a-z0-9._]{3,32}$/;

    if (!regex.test(value) && value.length > 0) {
      if (/[A-Z]/.test(value)) {
        setError(true);
        setErrorMessage("Discord username cannot contain capital letters");
        return false;
      }

      if (/[^a-z0-9._]/.test(value)) {
        setError(true);
        setErrorMessage(
          "Discord username can only contain lowercase letters, numbers, periods, and underscores"
        );
        return false;
      }

      if (value.length < 3) {
        setError(true);
        setErrorMessage("Discord username must be at least 3 characters");
        return false;
      }

      if (value.length > 32) {
        setError(true);
        setErrorMessage("Discord username cannot exceed 32 characters");
        return false;
      }
    } else if (value.length === 0) {
      setError(false);
      setErrorMessage("");
      return true;
    } else {
      setError(false);
      setErrorMessage("");
      return true;
    }
  };

  return <>
    <CustomLoader open={showLoader} />
    <main className="tw-animate-fade-in tw-rounded-lg" style={{ position: "relative", isolation: "isolate", width: "100%", minHeight: "calc(100vh - 120px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "42px 16px 64px", boxSizing: "border-box", overflow: "hidden", background: "#0d0f12" }}>
      <svg aria-hidden="true" viewBox="0 0 1200 680" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, zIndex: -3, width: "100%", height: "100%", pointerEvents: "none" }}>
        <defs>
          <pattern id="link-discord-dots" x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="#78d9de" fillOpacity="0.16" />
          </pattern>
        </defs>
        <rect x="210" y="105" width="780" height="470" rx="235" fill="url(#link-discord-dots)" opacity="0.55" />
        <line x1="0" y1="295" x2="1200" y2="295" stroke="#ffffff" strokeOpacity="0.055" />
        <line x1="0" y1="345" x2="1200" y2="345" stroke="#ffffff" strokeOpacity="0.045" />
        <line x1="0" y1="395" x2="1200" y2="395" stroke="#ffffff" strokeOpacity="0.035" />
        <circle cx="315" cy="272" r="2" fill="#00ADB5" fillOpacity="0.38" />
        <circle cx="895" cy="362" r="2" fill="#00ADB5" fillOpacity="0.32" />
        <circle cx="350" cy="430" r="1.5" fill="#ffffff" fillOpacity="0.22" />
        <circle cx="845" cy="238" r="1.5" fill="#ffffff" fillOpacity="0.18" />
      </svg>
      <div aria-hidden="true" style={{ position: "absolute", zIndex: -2, width: 430, height: 430, borderRadius: "50%", background: "rgba(0,173,181,.065)", filter: "blur(95px)" }} />
      <Card style={{ width: "100%", maxWidth: 450, position: "relative", background: "rgba(24,24,26,.72)", backdropFilter: "blur(28px) saturate(125%)", WebkitBackdropFilter: "blur(28px) saturate(125%)", border: "1px solid rgba(255,255,255,.14)", boxShadow: "0 35px 90px -28px rgba(0,0,0,.95), inset 0 1px rgba(255,255,255,.055), 0 0 0 1px rgba(0,173,181,.025)", overflow: "hidden" }}>
        <div aria-hidden="true" style={{ position: "absolute", inset: "0 0 auto", height: 1, background: "rgba(132,238,243,.48)" }} />
        <CardHeader style={{ display: "block", padding: "26px 26px 20px", borderBottom: "1px solid rgba(255,255,255,.065)", position: "relative" }}>
          <Button variant="ghost" size="icon-sm" type="button" aria-label="Close" onClick={() => navigate(-1)} style={{ color: "#8b9296", position: "absolute", right: 16, top: 16 }}>
            <X size={17} />
          </Button>
          <div style={{ width: 52, height: 52, display: "grid", placeItems: "center", borderRadius: 15, background: "rgba(255,255,255,.075)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,.13)", boxShadow: "inset 0 1px rgba(255,255,255,.07), 0 12px 28px -18px rgba(0,0,0,.9)" }}>
            <img src={discord} alt="Discord" style={{ width: 29, height: 29 }} />
          </div>
          <CardTitle className="!text-white" style={{color:"#fff", marginTop: 18, fontSize: 22, lineHeight: 1.22, letterSpacing: "-.025em" }}>Link Your Discord Account</CardTitle>
          <CardDescription style={{ marginTop: 7, paddingRight: 12, color: "#aeb4b8", fontSize: 13, lineHeight: 1.55 }}>Connect your Discord account to enable seamless communication and notifications.</CardDescription>
        </CardHeader>
        <CardContent style={{ padding: 26 }}>
          {showMessage && <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: 14, marginBottom: 22, borderRadius: 12, color: "#f7d0c8", background: "rgba(242,112,89,.1)", border: "1px solid rgba(242,112,89,.28)" }}>
            <AlertTriangle size={18} style={{ marginTop: 1, flexShrink: 0, color: "#f27059" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, overflowWrap: "anywhere" }}>{statusMessage || "Oops Something Went Wrong"}</p>
              {statusCode === "DIS_4" && <Button type="button" size="sm" onClick={handleUpdateDiscordRole} style={{ marginTop: 12 }}><CheckCircle2 size={15} /> Update Discord username</Button>}
            </div>
          </div>}
          <Label htmlFor="discordId" style={{ color: "#e9ebec", fontSize: 12.5 }}>Discord Username</Label>
          <div style={{ position: "relative" }}>
            <Input id="discordId" type="text" value={discordId} placeholder="Enter Discord Username" aria-invalid={error} aria-describedby={error ? "discordId-error" : undefined} onChange={(event) => { setDiscordId(event.target.value); validateDiscordUsername(event.target.value); }} style={{ paddingLeft: 42, borderColor: error ? "rgba(242,112,89,.65)" : undefined }} />
            <span aria-hidden="true" style={{ position: "absolute", left: 15, top: "50%", transform: "translateY(-50%)", color: error ? "#f27059" : "#8b9296", fontSize: 15, fontWeight: 700 }}>@</span>
          </div>
          {error && <p id="discordId-error" style={{ margin: "8px 0 0", color: "#f27059", fontSize: 12.5 }}>{errorMessage}</p>}
          <Separator style={{ margin: "21px 0 16px", background: "rgba(255,255,255,.08)" }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Button variant="outline" size="lg" type="button" onClick={() => navigate(-1)} style={{ width: "100%", borderColor: "rgba(255,255,255,.13)", borderRadius: 9 }}>Back</Button>
            <Button size="lg" type="button" onClick={handleRedirectDiscord} disabled={discordId.length < 3 || error} style={{ width: "100%", borderRadius: 9, fontWeight: 700,gap:"10px", boxShadow: discordId.length >= 3 && !error ? "0 10px 24px -14px rgba(0,173,181,.95)" : "none" }}>
              Link with Discord <ExternalLink size={14} />
            </Button>
          </div>
        </CardContent>
      </Card>
    </main>
  </>;
}
